import { ref } from "vue";

import { authService } from "../services/auth";
import { getUser, type StoredUser } from "../utils/auth";

export interface CurrentUserProfile extends StoredUser {
  id?: number;
  warehouse_id?: number;
  warehouse_code?: string;
  warehouse_name?: string;
  branch_name?: string;
  biller_name?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  email?: string;
  phone?: string;
  address?: string;
  company?: string;
  avatar?: string | null;
  gender?: number;
  active?: number | boolean;
  view_right?: number | boolean;
  edit_right?: number | boolean;
}

const currentUser = ref<CurrentUserProfile | null>(null);

const cacheCurrentUser = (user: CurrentUserProfile | null) => {
  currentUser.value = user;

  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  }
};

export const useCurrentUser = () => {
  const getCachedCurrentUser = (): CurrentUserProfile | null => {
    if (currentUser.value) {
      return currentUser.value;
    }

    const cachedUser = getUser() as CurrentUserProfile | null;
    currentUser.value = cachedUser;
    return cachedUser;
  };

  const fetchCurrentUser = async (forceRefresh = false): Promise<CurrentUserProfile | null> => {
    if (!forceRefresh) {
      const cachedUser = getCachedCurrentUser();
      if (cachedUser) {
        return cachedUser;
      }
    }

    try {
      const user = (await authService.getCurrentUser()) as CurrentUserProfile;
      cacheCurrentUser(user);
      return user;
    } catch (error) {
      console.error("Failed to fetch current user profile:", error);
      return getCachedCurrentUser();
    }
  };

  return {
    currentUser,
    getCachedCurrentUser,
    fetchCurrentUser,
    cacheCurrentUser,
  };
};
